export const enSettings = {
  title: "Settings",
  subtitle: "Manage tenant configuration and integrations.",
  tabs: {
    aria: "Settings sections",
    sso: "SSO",
    businessContext: "Business Context",
    llm: "LLM",
    tokenUsage: "Token Usage",
  },
  tokenUsage: {
    loading: "Loading token usage",
    description:
      "Total LLM tokens this tenant has used, across every feature. Figures are all-time and cover both Olive's keys and any of your own.",
    empty: "No token usage recorded yet.",
    tokensHeader: "Total tokens",
    inputHeader: "Input tokens",
    outputHeader: "Output tokens",
    totals: {
      total: "Total tokens",
      input: "Input tokens",
      output: "Output tokens",
      calls: "Provider calls",
    },
    byFeature: {
      title: "By feature",
      nameHeader: "Feature",
      countHeader: "Runs",
      // The drilldown's count column switches meaning to provider calls, since one run can use
      // several models. Named in the aria labels so a screen reader is not left guessing.
      showModels: "Show models for {{name}}",
      hideModels: "Hide models for {{name}}",
    },
    groups: {
      // "viewing" vs "building" rather than one "Apps" line: these two costs scale with different
      // things, and that difference is the reason the grouping exists.
      publishedApps: "Published apps — viewing",
      appStudio: "App Studio — building",
      chat: "Chat",
      semanticModel: "Semantic model",
      nuggets: "Nuggets",
    },
    byModel: {
      title: "By model",
      nameHeader: "Model",
      // Calls, not runs: one run can use several models, so these do not sum to the run count.
      countHeader: "Calls",
    },
    features: {
      chatTurn: "Chats",
      nuggetGraphRun: "Nugget runs",
      semanticModelGeneration: "Semantic model generation",
      semanticCandidates: "Semantic candidates",
      chatSummary: "Chat summaries",
      learningInsights: "Learning insights",
      appItemSummary: "App AI summaries",
      appQueryNaming: "Query naming",
      appChartDesign: "Chart design",
      appWidgetDraft: "App Studio widget drafts",
      semanticInterview: "Semantic interview",
      nuggetActionImage: "Nugget action images",
    },
    errors: {
      loadFailed: "Could not load token usage.",
    },
  },
  llm: {
    loading: "Loading LLM API keys",
    description:
      "Add your own OpenAI or Anthropic API keys. Requests for the models you select will use your key, and all other requests continue to use Olive's keys.",
    empty: "No API keys configured. All requests currently use Olive's keys.",
    addTitle: "Add an API key",
    editTitle: "Edit API key",
    configured: "Configured",
    providers: {
      openai: "OpenAI",
      anthropic: "Anthropic",
    },
    modelKinds: {
      chat: "Chat",
      embedding: "Embeddings",
      image: "Image",
    },
    fields: {
      provider: "Provider",
      name: "Name",
      models: "Models this key may serve",
      apiKey: "API key",
      apiKeyReplace: "Replace API key",
    },
    placeholders: {
      name: "Production",
      models: "Select models",
      apiKeyEdit: "Leave blank to keep the current key",
    },
    helpers: {
      name: "A label to identify this key.",
      models: "The same model may be attached to more than one key.",
      apiKey: "Stored encrypted and never displayed again after saving.",
      apiKeyEdit: "Leave blank to keep the current key, or enter a new key to replace it.",
      providerLocked: "The provider cannot be changed. Delete this key and add a new one instead.",
    },
    modal: {
      closeAria: "Close",
      cancel: "Cancel",
    },
    actions: {
      // Distinct from `save` on purpose: the trigger stays in the DOM behind the open dialog, so a
      // shared label would leave two identically named buttons on the page.
      newKey: "New key",
      save: "Add key",
      saveChanges: "Save changes",
      saveSucceeded: "API key saved.",
      updateSucceeded: "API key updated.",
      edit: "Edit API key",
      delete: "Delete API key",
    },
    delete: {
      title: "Delete this API key?",
      body: "\"{{name}}\" will be removed. Any model it serves falls back to Olive's own key.",
      cancel: "Cancel",
      confirm: "Delete",
    },
    errors: {
      loadFailed: "Could not load LLM API keys.",
      saveFailed: "Could not save the API key.",
      updateFailed: "Could not update the API key.",
      deleteFailed: "Could not delete the API key.",
    },
  },
  sso: {
    loading: "Loading SSO configuration",
    sections: {
      samlConfiguration: "SAML configuration",
      identityProvider: "Enter the information from your Identity provider",
      userAttributes: "User attributes:",
      metadata: "Metadata",
      configurations: "SSO configurations",
    },
    fields: {
      name: "Name",
      entityId: "Identifier (Entity ID)",
      replyUrl: "Reply URL (Assertion Consumer Service URL)",
      givenNameAttribute:
        "Given name (e.g. https://schemas…/givenname)",
      emailAttribute: "Email (e.g. https://schemas…/email address)",
      metadataUrl: "URL",
    },
    placeholders: {
      name: "Type name here",
      attributeUrl: "Copy paste here",
      metadataUrl: "https://…",
    },
    state: {
      configured: "Configured",
      enabled: "Enabled",
    },
    copyAria: "Copy to clipboard",
    copiedAria: "Copied",
    actions: {
      create: "Create",
      createSucceeded: "SSO configuration created.",
      enable: "Enable SSO",
    },
    validation: {
      required: "This field is required.",
      invalidUrl: "Enter a valid URL.",
      maxLength: "Must be 512 characters or fewer.",
      nameRequired: "Name is required",
      nameMaxLength: "Name must be 32 characters or fewer",
      nameInvalidCharacters: "Name must not contain spaces or underscores",
    },
    errors: {
      loadFailed: "Unable to load SSO configurations.",
      createFailed: "Unable to create SSO configuration.",
      enableFailed: "Unable to enable SSO.",
    },
    cognito: {
      loading: "Loading SAML configuration…",
      unavailable: "SAML configuration unavailable.",
    },
    credentialLogin: {
      sectionTitle: "Sign-in methods",
      enabled:
        "Users can sign in with their email and password, or through your identity provider.",
      disabled:
        "Email and password sign-in is turned off. Users sign in through your identity provider only.",
      action: "Turn off email and password sign-in",
      confirm: {
        title: "Turn off email and password sign-in?",
        body: "Everyone will sign in through your identity provider from now on. Confirm that signing in through it works before you continue — if it does not, no one will be able to get in. This cannot be undone from Settings.",
        cancel: "Cancel",
        submit: "Turn off",
      },
      errors: {
        disableFailed: "Unable to turn off email and password sign-in.",
      },
    },
  },
  businessContext: {
    loading: "Loading business context",
    fields: {
      industry: "Industry",
      companyOverview: "Company overview",
      companyHistory: "Company history",
    },
    placeholders: {
      industry: "e.g. Video games / interactive entertainment",
      companyOverview: "What the company does, product lines, business model",
      companyHistory: "Founding story, pivots, acquisitions",
    },
    actions: {
      save: "Save",
      saveSucceeded: "Business context saved.",
    },
    errors: {
      loadFailed: "Unable to load business context.",
      saveFailed: "Unable to save business context.",
    },
  },
} as const;
